import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import ApiCall, {
  refreshAccessToken,
  setSessionExpiredHandler,
} from "../Api/ApiCall";
import {
  setAccessToken,
  clearAccessToken,
  getAccessToken,
} from "../Api/tokenStore";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  // The access token lives in memory, so on first render there is never one.
  // `bootstrapping` keeps the app from deciding "logged out" before the
  // refresh cookie has had a chance to produce a token.
  const [isLogin, setIsLogin] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(true);

  const inactivityTimeoutRef = useRef(null);

  const clearInactivityTimer = useCallback(() => {
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
      inactivityTimeoutRef.current = null;
    }
  }, []);

  /**
   * Clears the local session.
   *
   * `revokeOnServer` calls POST /user/userLogout, which marks the refresh
   * token revoked and clears the httpOnly cookie. Without it the refresh
   * token stayed valid for up to 30 days after the user "logged out" - the
   * server endpoint existed and worked, it was simply never called.
   */
  const logout = useCallback(async ({ revokeOnServer = true } = {}) => {
    if (revokeOnServer) {
      try {
        await ApiCall("POST", "user/userLogout", {}, "application/json");
      } catch (err) {
        // Never trap the user in a session because the network failed.
        console.warn("Server logout failed; clearing local session anyway");
      }
    }

    const keepMeLoggedIn = localStorage.getItem("keepMeLoggedIn") === "true";

    clearAccessToken();
    localStorage.clear();

    if (keepMeLoggedIn) {
      localStorage.setItem("keepMeLoggedIn", "true");
    }

    setIsLogin(false);
    clearInactivityTimer();
  }, [clearInactivityTimer]);

  const resetInactivityTimer = useCallback(() => {
    clearInactivityTimer();
    const keepMeLoggedIn = localStorage.getItem("keepMeLoggedIn") === "true";

    if (!keepMeLoggedIn) {
      inactivityTimeoutRef.current = setTimeout(() => {
        logout();
      }, 3600000); // 1 hour
    }
  }, [clearInactivityTimer, logout]);

  const login = useCallback((token) => {
    if (token !== undefined && token !== null) {
      setAccessToken(token);
      setIsLogin(true);
      resetInactivityTimer();
    }
  }, [resetInactivityTimer]);

  const trackActivity = useCallback(() => {
    if (isLogin) {
      resetInactivityTimer();
    }
  }, [isLogin, resetInactivityTimer]);

  const getRefreshToken = useCallback(async () => {
    try {
      await refreshAccessToken();
      setIsLogin(true);
      return { statusCode: 200 };
    } catch (error) {
      // No usable refresh cookie: this is a logged-out visitor, not an error.
      clearAccessToken();
      setIsLogin(false);
      return error?.response;
    }
  }, []);

  // A 401 that survives a refresh attempt ends the session. Registered once so
  // the API layer does not need to import this context.
  useEffect(() => {
    setSessionExpiredHandler(() => {
      clearAccessToken();
      setIsLogin(false);
    });
  }, []);

  // Recover the session from the httpOnly refresh cookie on load.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      await getRefreshToken();
      if (!cancelled) setBootstrapping(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [getRefreshToken]);

  useEffect(() => {
    if (isLogin) {
      window.addEventListener("mousemove", trackActivity);
      window.addEventListener("keydown", trackActivity);
      resetInactivityTimer();

      // Refresh ahead of the 15-minute access token expiry. The response
      // interceptor also refreshes reactively on a 401, so a missed tick no
      // longer drops a request on the floor.
      const refreshInterval = setInterval(() => {
        getRefreshToken();
      }, 840000);

      return () => {
        clearInterval(refreshInterval);
        window.removeEventListener("mousemove", trackActivity);
        window.removeEventListener("keydown", trackActivity);
        clearInactivityTimer();
      };
    }

    return () => {
      window.removeEventListener("mousemove", trackActivity);
      window.removeEventListener("keydown", trackActivity);
      clearInactivityTimer();
    };
  }, [isLogin, trackActivity, resetInactivityTimer, getRefreshToken, clearInactivityTimer]);

  const contextValue = useMemo(() => ({
    isLogin,
    bootstrapping,
    login,
    logout,
    getRefreshToken,
    getAccessToken,
  }), [isLogin, bootstrapping, login, logout, getRefreshToken]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
